import { httpService, upsertItemToArray } from '@/utils';
import { VirtualMailboxAddress } from './use-virtual-mailbox-addresses';
import { Mail } from './mail';
import { computed, reactive } from 'vue';

type ShipmentMail = {
	id: number;
	external_id: number;
	mail: Mail;
	updated_at: string;
	created_at: string;
};

export type VirtualMailboxShipment = {
	id: number;
	external_id: number;
	status: 'initiated' | 'pending';
	address: VirtualMailboxAddress;
	pieces_count: number;
	finished_at: null | string;
	locked: boolean;
	created_at: string;
	updated_at: string;
};

type VirtualMailboxShipmentsState = {
	isLoading: boolean;
	shipments: VirtualMailboxShipment[];
};

const state = reactive<VirtualMailboxShipmentsState>({
	isLoading: true,
	shipments: []
});

export function useVirtualMailboxShipments() {
	async function loadShipments(mailboxId: number) {
		state.isLoading = true;
		try {
			state.shipments = await httpService.get<
				VirtualMailboxShipment[]
			>(
				`/company/virtual-mailbox/items/${mailboxId}/shipment`
			);
		} catch (e) {
			console.log('Failed loading shipments');
		} finally {
			state.isLoading = false;
		}
	}

	async function addShipmentPiece(
		mailboxId: number,
		shipmentId: number,
		mail_id: number
	) {
		await httpService.post<VirtualMailboxShipment>(
			`/company/virtual-mailbox/items/${mailboxId}/shipment/pieces/${shipmentId}`,
			{ mail_id }
		);
		const shipment = state.shipments.find(
			(s) => s.id === shipmentId
		);

		if (!shipment) {
			return;
		}

		state.shipments = upsertItemToArray(
			state.shipments,
			{
				...shipment,
				pieces_count: shipment.pieces_count + 1
			}
		);
		return shipment;
	}

	async function removeShipmentPiece(
		mailboxId: number,
		shipmentId: number,
		mail_id: number
	) {
		await httpService.delete<VirtualMailboxShipment>(
			`company/virtual-mailbox/items/${mailboxId}/shipment/pieces/${shipmentId}/${mail_id}`
		);

		const shipment = state.shipments.find(
			(s) => s.id === shipmentId
		);

		if (!shipment) {
			return;
		}

		state.shipments = upsertItemToArray(
			state.shipments,
			{
				...shipment,
				pieces_count:
					shipment.pieces_count === 0
						? 0
						: shipment.pieces_count - 1
			}
		);
		return shipment;
	}

	async function createShipment(
		mailboxId: number,
		address_id: number
	) {
		const shipment =
			await httpService.post<VirtualMailboxShipment>(
				`/company/virtual-mailbox/items/${mailboxId}/shipment`,
				{
					address_id
				}
			);

		state.shipments = upsertItemToArray(
			state.shipments,
			shipment
		);
	}

	async function finishShipment(
		mailboxId: number,
		shipmentId: number
	) {
		const shipment =
			await httpService.post<VirtualMailboxShipment>(
				`/company/virtual-mailbox/items/${mailboxId}/shipment/${shipmentId}/finish`
			);
		state.shipments = upsertItemToArray(
			state.shipments,
			shipment
		);
		return shipment;
	}

	return {
		loadShipments,
		createShipment,
		finishShipment,
		isLoading: computed(() => state.isLoading),
		shipments: computed(() => state.shipments),
		addShipmentPiece,
		removeShipmentPiece
	};
}
